import React, { useCallback, useEffect, useState } from 'react';
import { Form, Avatar, Upload, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { HugeiconsIcon } from '@hugeicons/react';
import { CancelCircleIcon, CheckmarkCircleIcon, Edit02Icon, UserCircleIcon } from '@hugeicons-pro/core-bulk-rounded';
import css from './ManagementProfile.module.css';
import sharedCss from '../BusinessDetails/GroupDetail.module.css';
import { getSignedUrl, updateUserProfile, uploadToS3 } from '../../../services';
import { getCallingCodeOfCountry, getErrorMessage, standardizePhoneNumber } from '../../../utils';
import i18n from 'i18next';
import { useReloadCurrentUser, useCurrentUser } from '../../../context/hooks';
import { Page } from '../../../components/Theme/Page';
import { PageTitle } from '../../../components/Theme/PageTitle';
import { SNotification } from '../../../services/notification';
import { StringUtils } from '../../../utils/string';
import { FormItem } from '../../../components/Form/FormItem';
import FormInput from '../../../components/Form/FormInput';
import FormSelectCountry from '../../../components/Form/FormSelectCountry/FormSelectCountry';
import ChangePasswordForm from '../BusinessDetails/ChangePasswordForm';
import PhoneNumberFields from '../../../components/Form/PhoneNumberFields';

type ManagementProfileProps = {
  variant?: 'page' | 'modal';
};

const ManagementProfile = ({ variant = 'page' }: ManagementProfileProps) => {
  const reloadCurrentUser = useReloadCurrentUser();
  const currentUser = useCurrentUser();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSubmitForm = useCallback(
    async (userInfo) => {
      setEditing(false);
      // Local spinner only: the global loading overlay would blank the whole screen.
      setSaving(true);
      try {
        const { firstName, lastName, phoneNumberUserInput, phoneNumberCountryCode } = userInfo;

        // Update user profile (firstName, lastName, phoneNumber)
        let phoneNumber = '';
        if (phoneNumberUserInput && phoneNumberCountryCode) {
          const standardizedInput = `${standardizePhoneNumber(phoneNumberUserInput)}`;
          const phoneNumberCallingCode = getCallingCodeOfCountry(phoneNumberCountryCode);
          phoneNumber = `+${phoneNumberCallingCode}${parseInt(standardizedInput)}`;
        }

        await updateUserProfile({
          firstName,
          lastName,
          phoneNumber,
          phoneNumberUserInput,
          phoneNumberCountryCode,
        });

        SNotification.success(StringUtils.toFirstCap(i18n.t('msg:updateSuccess')));
        reloadCurrentUser(false);
      } catch (error) {
        SNotification.error(getErrorMessage(error));
      } finally {
        setSaving(false);
      }
    },
    [reloadCurrentUser],
  );

  const uploadAvatar = useCallback(
    async ({ file, onError, onProgress }) => {
      // Local spinner only: the global loading overlay would blank the whole screen.
      setUploadingAvatar(true);
      try {
        onProgress({ percent: 0 });
        const signedUrl = await getSignedUrl(file.name, file.size);
        await uploadToS3(signedUrl, file);
        const finalUrl = signedUrl.substring(0, signedUrl.indexOf('?'));

        await updateUserProfile({ avatar: finalUrl });
        setAvatarUrl(finalUrl);
        SNotification.success('Avatar updated successfully!');
        reloadCurrentUser(false);
      } catch (err) {
        SNotification.error(getErrorMessage(err));
        onError(err);
      } finally {
        setUploadingAvatar(false);
      }
    },
    [reloadCurrentUser],
  );

  const beforeAvatarUpload = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    return isJpgOrPng;
  };

  const resetFormValues = useCallback(() => {
    if (currentUser) {
      form.setFieldsValue({
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        email: currentUser.email,
        phoneNumberCountryCode: currentUser.phoneNumberCountryCode,
        phoneNumberUserInput: currentUser.phoneNumberUserInput,
      });
      setAvatarUrl(currentUser.avatar || '');
    }
  }, [form, currentUser]);

  useEffect(() => {
    // Don't overwrite what the user is typing (e.g. after an avatar upload reloads the user).
    if (editing) return;
    resetFormValues();
  }, [currentUser, editing, resetFormValues]);

  const renderFooterActions = () => (
    <div className={sharedCss.cardFooter}>
      {!editing && !saving && (
        <button type="button" className={sharedCss.btnSecondary} onClick={() => setEditing(true)}>
          <HugeiconsIcon icon={Edit02Icon} size={15} color="#374151" />
          Edit
        </button>
      )}
      {(editing || saving) && (
        <>
          <button
            type="button"
            className={sharedCss.btnSecondary}
            disabled={saving}
            onClick={() => {
              resetFormValues();
              setEditing(false);
            }}
          >
            <HugeiconsIcon icon={CancelCircleIcon} size={15} color="#374151" />
            Cancel
          </button>
          <button type="submit" className={sharedCss.btnPrimary} disabled={saving}>
            {saving ? <LoadingOutlined spin style={{ fontSize: 15, color: '#fff' }} /> : <HugeiconsIcon icon={CheckmarkCircleIcon} size={15} color="#fff" />}
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      )}
    </div>
  );

  const formContent = (
    <div className={css.body}>
      <div className={classNames(css.column1, { [css.column1Modal]: variant === 'modal' })}>
        <div className={sharedCss.profileStack}>
          <Form validateTrigger={false} onFinish={onSubmitForm} form={form}>
            <div className={sharedCss.settingsSection}>
              <div className={sharedCss.settingsAside}>
                <div className={sharedCss.settingsAsideTitle}>Personal info</div>
                <div className={sharedCss.settingsAsideDesc}>Update your photo and personal details.</div>
              </div>

              <div className={classNames(sharedCss.card, sharedCss.formCard)}>
                <div className={sharedCss.cardBody}>
                  <div className={sharedCss.personalAvatarRow}>
                    <Avatar
                      className={sharedCss.profileAvatar}
                      size={80}
                      src={avatarUrl || undefined}
                      icon={!avatarUrl ? <HugeiconsIcon icon={UserCircleIcon} size={40} color="#94a3b8" /> : undefined}
                    />
                    <div className={sharedCss.avatarCardBody}>
                      <div className={sharedCss.avatarTitle}>Profile Avatar</div>
                      <div className={sharedCss.avatarSubtitle}>Upload a profile picture.</div>
                      <Upload name="avatar" listType="picture" showUploadList={false} customRequest={uploadAvatar} beforeUpload={beforeAvatarUpload} disabled={uploadingAvatar}>
                        <button type="button" className={sharedCss.btnPrimary} disabled={uploadingAvatar}>
                          {uploadingAvatar && <LoadingOutlined spin style={{ fontSize: 15, color: '#fff' }} />}
                          {uploadingAvatar ? 'Uploading...' : avatarUrl ? 'Change Avatar' : 'Upload Avatar'}
                        </button>
                      </Upload>
                    </div>
                  </div>

                  {/* Only email is required; first/last name, avatar and phone number are optional */}
                  <FormItem name="firstName">
                    <FormInput label={i18n.t('label:firstName')} disabled={!editing} />
                  </FormItem>
                  <FormItem name="lastName">
                    <FormInput label={i18n.t('label:lastName')} disabled={!editing} />
                  </FormItem>

                  <FormItem name="email" rules={[{ required: true, message: i18n.t('msg:pleaseInput', { field: i18n.t('label:email') }), type: 'email' }]}>
                    <FormInput label={i18n.t('label:email')} disabled />
                  </FormItem>

                  <PhoneNumberFields disabled={!editing} optional />
                </div>
                {renderFooterActions()}
              </div>
            </div>
          </Form>

          <ChangePasswordForm />
        </div>
      </div>
    </div>
  );

  if (variant === 'modal') return formContent;

  return (
    <Page large={true}>
      <PageTitle center={true}>Profile</PageTitle>
      {formContent}
    </Page>
  );
};

export default ManagementProfile;
